import styled, { css } from 'styled-components';
import { AdvancedSelectProps } from '.';

export const Container = styled.div<{ $disabled: AdvancedSelectProps['disabled']; $size: AdvancedSelectProps['size'] }>`
  ${({ theme: { fontSizes }, $disabled, $size = 'md' }) => css`
    ${$disabled &&
    css`
      cursor: not-allowed;

      & * {
        cursor: not-allowed;
      }

      &:hover {
        cursor: not-allowed;
      }
    `}

    ${$size === 'sm' &&
    css`
      font-size: ${fontSizes.s14};

      .icon {
        height: 1rem;
        padding: 0.5rem;
        width: 1rem;
      }
    `}

    ${$size === 'md' &&
    css`
      font-size: ${fontSizes.s16};

      .icon {
        height: 1.125rem;
        padding: 0.5625rem;
        width: 1.125rem;
      }
    `}

    ${$size === 'lg' &&
    css`
      font-size: ${fontSizes.s20};

      .icon {
        height: 1.25rem;
        padding: 0.625rem;
        width: 1.25rem;
      }
    `}

    ${$size === 'xl' &&
    css`
      font-size: ${fontSizes.s24};

      .icon {
        height: 1.5rem;
        padding: 0.75rem;
        width: 1.5rem;
      }
    `}
  `}
`;

export const Display = styled.div<{
  $disabled: AdvancedSelectProps['disabled'];
  $hasIcon: boolean;
  $isCollapsed: boolean;
  $maxWidth: AdvancedSelectProps['maxWidth'];
}>`
  ${({ theme: { colors }, $disabled, $hasIcon, $isCollapsed, $maxWidth }) => css`
    align-items: center;
    border-radius: 0.25rem;
    box-shadow: inset 0 0 0 1px ${colors.brandDarkTint80};
    color: ${colors.brandDark};
    cursor: ${$disabled ? 'not-allowed' : 'pointer'};
    display: flex;

    svg {
      flex-shrink: 0;
    }

    span {
      display: -webkit-box;
      overflow: hidden;
      max-width: ${$maxWidth || 'unset'};
      font-size: 1em;
      text-align: left;
      text-overflow: ellipsis;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;

      ${!$hasIcon &&
      css`
        padding-left: 0.8em;
      `}
    }

    .ds-advanced-select-display-chevron {
      ${$isCollapsed &&
      css`
        transform: scaleY(-1);
      `}
    }

    * {
      user-select: none;
    }

    ${$disabled &&
    css`
      background: ${colors.brandDarkAlpha5};
      color: ${colors.brandDarkAlpha60};
      pointer-events: none;
      box-shadow: unset;

      .icon {
        path {
          stroke: currentColor;
        }
      }
    `}
  `}
`;

export const List = styled.div<{ $minWidth?: number; $useMaxWidth: boolean }>`
  ${({ theme: { colors }, $minWidth, $useMaxWidth }) => css`
    background: ${colors.brandLight};
    border-radius: 0.25rem;
    box-shadow: 0 0 0 1px ${colors.brandDarkTint80}, 0 0.75rem 0.5rem -0.25rem rgba(32, 30, 36, 0.15),
      0 0.25rem 0.25rem -0.125rem rgba(32, 30, 36, 0.2);
    margin: 0;
    min-width: ${$minWidth}px;
    width: max-content;

    ${$useMaxWidth &&
    css`
      width: 18rem;
    `}

    ul {
      display: flex;
      flex-direction: column;
      margin: 0;
      padding: 0.5rem;
      max-height: 11.375rem;
      overflow-y: auto;
      overflow-x: hidden;

      &::-webkit-scrollbar {
        border-radius: 8px;
        height: 8px;
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        background: white;
        border-radius: 8px;
      }

      &::-webkit-scrollbar-thumb {
        background-clip: padding-box;
        background-color: ${colors.brandDarkTint80};
        border-radius: 8px;
        height: 8px;
        -webkit-border-radius: 8px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background-color: ${colors.brandDarkTint70};
      }
    }
  `}
`;

export const InputArea = styled.div<{ $hasError: boolean }>`
  ${({ theme: { colors }, $hasError }) => css`
    border-bottom: 1px solid ${colors.brandDarkTint80};

    .input {
      max-width: -webkit-fill-available;

      label {
        background: transparent;
        outline-offset: 2px;
        outline-width: 2px;
        padding: 0.5em 0;

        input {
          box-sizing: border-box;
          margin: 0;
          padding: 0 1rem;
        }

        .right-icon {
          display: none;
        }
      }

      .input-feedback {
        padding: 0.125rem 0.5rem;
        margin-bottom: 0.25rem;
      }
    }

    ${$hasError &&
    css`
      .input {
        label {
          outline-color: ${colors.semanticDanger} !important;
        }
      }
    `}
  `}
`;

export const ListItem = styled.li<{ $selected: boolean }>`
  ${({ theme: { borderRadius, colors, fontSizes }, $selected }) => css`
    align-items: center;
    border-radius: ${borderRadius.sm};
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    font-size: ${fontSizes.s14};
    line-height: 1em;
    min-height: 2.5rem;
    padding: 0.5rem 1rem;

    ${$selected &&
    css`
      background: ${colors.brandPrimaryAlpha5};
      color: ${colors.brandPrimary};
    `}

    &:hover {
      background: ${colors.brandPrimaryAlpha5};
      color: ${colors.brandPrimary};
    }

    &:focus-within,
    &:focus {
      color: ${colors.brandPrimary};
      outline-color: ${colors.brandPrimary};
      outline-offset: 2px;
      outline-width: 1px;
    }
  `}
`;

export const StackableArea = styled.div<{ $inputWidth: number }>`
  ${({ theme: { colors, fontSizes }, $inputWidth }) => css`
    align-items: flex-start;
    border-bottom: 1px solid ${colors.brandDarkTint80};
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.75rem 1rem 0.5rem;

    input {
      border: 0;
      color: ${colors.brandDark};
      font-size: ${fontSizes.s14};
      height: 1.625rem;
      margin: 0;
      outline: unset;
      padding: 0;
      width: ${$inputWidth + 8}px;

      &::placeholder {
        color: ${colors.brandDarkTint50};
      }

      &:focus-within {
        background: ${colors.brandPrimaryAlpha5};
      }
    }
  `}
`;

export const WidthRef = styled.span`
  ${({ theme: { fontSizes } }) => css`
    font-size: ${fontSizes.s14};
    opacity: 0;
    pointer-events: none;
    position: absolute;
    user-select: none;
    z-index: 0;
  `}
`;

export const SelectedOption = styled.div`
  ${({ theme: { borderRadius, colors, fontSizes, fontWeights } }) => css`
    align-items: center;
    background: ${colors.brandDarkTint30};
    border-radius: ${borderRadius.sm};
    cursor: default;
    display: flex;
    gap: 0.25rem;
    padding: 0.25rem 0.375rem;
    user-select: none;

    span {
      color: ${colors.brandLight};
      display: -webkit-box;
      overflow: hidden;
      max-width: 15rem;
      font-size: ${fontSizes.s14};
      font-weight: ${fontWeights.medium};
      text-align: left;
      text-overflow: ellipsis;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
    }

    button {
      flex-shrink: 0;
      aspect-ratio: 1 / 1;
      height: 1.125rem;
      min-width: unset;
      width: 1.125rem;

      svg {
        height: 1.125rem;
        width: 1.125rem;

        path {
          stroke: ${colors.brandLight};
          stroke-width: 1.5;
        }
      }
    }
  `}
`;

export const Footer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: 0.5rem 1rem;
`;

export const Spacer = styled.span`
  ${({ theme: { colors } }) => css`
    display: block;
    background: ${colors.brandDarkAlpha10};
    min-height: 1px;
    margin: 0.5rem 0;
    margin-left: -0.5rem;
    width: calc(100% + 1rem);
  `}
`;
